import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import SimplizeLogo from "../../../../components/SimplizeLogo";
import OnboardingQuestionnaire from "../components/onboarding/OnboardingQuestionnaire";
import OnboardingLoading from "../components/onboarding/OnboardingLoading";
import UserMenuPopover from "../../../../components/UserMenuPopover";
import UserSettingsModal from "../../../../components/UserSettingsModal";
import { authApi, userApi, getApiErrorMessage } from "../../../../utils/api";
import { clearStorageForLogout } from "../../../../utils/storageUtils";
import { User } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  useCreateProfileMutation,
  useGetJobStatusQuery,
} from "../../../api/nebula-profile/nebulaProfileApi";
import { useGetStatsQuery } from "../../../api/nebula-stats/nebulaStatsApi";
import { baseApi } from "../../../api/baseApi";
import { NEBULA_STATUS, isInitStatus } from "../utils/nebulaData";
import { Toaster, toast } from "react-hot-toast";

const CAPITAL_SIZE_MAP = {
  "50tr": 50_000_000,
  "100tr": 100_000_000,
  "500tr": 500_000_000,
  "1ty": 1_000_000_000,
};

const NebulaInitPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [step, setStep] = React.useState(1);
  const [answers, setAnswers] = React.useState({
    investingExperience: null,
    capitalAmount: null,
  });
  const [isCheckingProgress, setIsCheckingProgress] = React.useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = React.useState(false);
  const [isUserSettingsOpen, setIsUserSettingsOpen] = React.useState(false);

  const [createProfile] = useCreateProfileMutation();
  const currentUser = userApi.getCached();
  const { data: statsData, isLoading: isLoadingStats } = useGetStatsQuery({});

  const { data: jobStatusData } = useGetJobStatusQuery("initialize", {
    skip: !isCheckingProgress,
    pollingInterval: 2000,
  });

  React.useEffect(() => {
    if (isLoadingStats || !statsData?.data?.status) return;
    const status = statsData.data.status;
    if (status === NEBULA_STATUS.RUNNING) {
      setStep(2);
      setIsCheckingProgress(true);
    } else if (!isInitStatus(status)) {
      setStep(4);
    }
  }, [statsData, isLoadingStats]);

  React.useEffect(() => {
    if (!jobStatusData?.data) return;

    const { progressStatus, status } = jobStatusData.data;

    if (status === "SUCCESS" || progressStatus >= 100) {
      dispatch(baseApi.util.invalidateTags(["PortfolioStats"]));
      setStep(4);
      setIsCheckingProgress(false);
    } else if (progressStatus >= 50) {
      setStep(3);
    } else if (progressStatus > 0) {
      setStep(2);
    }
  }, [jobStatusData]);

  const handleLogout = () => {
    authApi.logout();
    userApi.clearCached();
    clearStorageForLogout();
    window.location.replace("/login");
  };

  const handleAnswer = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const handleNext = () => {
    if (step === 4) navigate("/portfolio-ai");
  };

  const handleCreatePortfolio = async () => {
    try {
      const payload = {
        investingExperience: answers.investingExperience,
        capitalAmount:
          CAPITAL_SIZE_MAP[answers.capitalAmount] ??
          parseInt(answers.capitalAmount, 10),
      };

      await createProfile(payload).unwrap();
      dispatch(baseApi.util.invalidateTags(["PortfolioStats"]));
      setStep(2);
      setIsCheckingProgress(true);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Có lỗi xảy ra, vui lòng thử lại sau ít phút."));
      console.error("Failed to create portfolio profile:", error);
    }
  };

  const handleSkip = () => {
    navigate("/");
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col overflow-y-auto transition-colors duration-500 bg-[#F8FDFC]">
      <Toaster position="top-right" />
      <header className="w-full h-[80px] px-[68px] flex items-center justify-between shrink-0">
        <SimplizeLogo className="h-[32px] w-auto" />
        <div className="relative">
          <button
            onClick={() => setIsUserMenuOpen((prev) => !prev)}
            className="w-[40px] h-[40px] rounded-full bg-white border border-[#ececed] flex items-center justify-center text-[#636369] hover:bg-gray-50 transition-colors"
          >
            <User className="w-[20px] h-[20px]" />
          </button>

          <UserMenuPopover
            isOpen={isUserMenuOpen}
            email={currentUser?.email || ""}
            className="absolute top-[48px] right-0"
            onSettings={() => {
              setIsUserMenuOpen(false);
              setIsUserSettingsOpen(true);
            }}
            onLogout={handleLogout}
            onClose={() => setIsUserMenuOpen(false)}
          />
        </div>
      </header>

      <UserSettingsModal
        isOpen={isUserSettingsOpen}
        onClose={() => setIsUserSettingsOpen(false)}
        user={currentUser}
      />

      <div className="flex-1 flex flex-col justify-center px-4">
        <div className="w-full flex justify-center relative">
          <AnimatePresence mode="wait">
            {isLoadingStats && (
              <motion.div
                key="stats-loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="w-full flex justify-center"
              />
            )}

            {!isLoadingStats && step === 1 && (
              <motion.div
                key="questionnaire"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="w-full flex justify-center"
              >
                <OnboardingQuestionnaire
                  answers={answers}
                  onAnswer={handleAnswer}
                  onFinalNext={handleCreatePortfolio}
                  onSkip={handleSkip}
                />
              </motion.div>
            )}

            {!isLoadingStats && step >= 2 && (
              <motion.div
                key="loading-steps"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="w-full flex justify-center"
              >
                <OnboardingLoading
                  step={step - 2}
                  onNext={handleNext}
                  answers={answers}
                  loadingText={jobStatusData?.data?.message}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default NebulaInitPage;
